import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  CircularProgress,
  Collapse,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { firstBy } from "thenby";
import { v4 } from "uuid";

import type {
  CreateExternalPatientDemograhicsPullEventRequest,
  ExternalPatientLink,
  ExternalPatientLinkEnhanced,
  OrganisationMembership,
  PatientTimelineResponse,
} from "@thalamos/aspire-common";
import {
  acdV1Base,
  admissionTemplateIds,
  baseTemplates,
  capitalize,
  doesUserMeetRequirement,
  findFormsMerged,
  getViewAndInviteRequirement,
  isCqcUserSession,
  isGuestUserSession,
  isMcaUserSession,
  isSoadUserSession,
  m2V1Base,
  s23V1Base,
} from "@thalamos/aspire-common";

import { api } from "../api.js";
import { FormCategoryRow } from "../components/FormCategoryRow.js";
import { LoggedInUserContext } from "../Contexts.js";
import {
  isNotFoundStatus,
  isRecordId,
  throwUnansweredReadFailure,
} from "../helpers/recordRead.js";
import { usePatientTimeline } from "../hooks/apiCalls.js";
import { useExternalPatientLinks } from "../hooks/ExternalPatientLink/useExternalPatientLinks.js";
import { routeFns } from "../routePaths.js";
import { AdmissionWarningDialog } from "./AdmissionWarningDialog.js";
import type { DefaultPageProps } from "./defaultProps.js";
import { canReconcileViaAspireFlag } from "./FormProgressPage/canReconcileViaAspireFlag.js";
import { PatientActivityLog } from "./FormProgressPage/helpers/ActivityLogs/PatientActivityLog.js";
import { FormContextPdfViewer } from "./FormProgressPage/helpers/FormContextPdfViewer.js";
import { NewFormDialog } from "./NewFormDialog.js";
import { PatientReconciliationSearchModal } from "./PatientSearchV2/PatientReconciliationSearchModal.js";
import { PatientTimelineCard } from "./PatientTimelineCard.js";
import {
  Button,
  FormTitle,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";
import { FormFooterSection } from "~/components/form/index.js";
import { PatientBanner } from "~/components/layout/index.js";
import { Container, ExpandMore } from "~/components/layout/styleWrappers.js";

interface FormAcc {
  [form: string]: {
    category: string;
    formName: string;
    description: string;
    section: string[];
  };
}

function PatientFormLaunch({
  patientTimeline,
  syncExternalPatientLink,
}: {
  patientTimeline: PatientTimelineResponse;
  syncExternalPatientLink?: (
    reason: CreateExternalPatientDemograhicsPullEventRequest["reason"],
  ) => Promise<void>;
}) {
  const navigate = useNavigate();

  const isNotAdmissionTemplate = (template: { id: string }): boolean => {
    return !admissionTemplateIds.includes(template.id);
  };

  const { category } = useParams();

  const categoryCaps = category
    ? [
        TREATMENT_CATEGORY,
        HOSPITAL_CATEGORY,
        ADMISSION_CATEGORY,
        DISCHARGE_CATEGORY,
        MISCELLANEOUS_CATEGORY,
      ].includes(category)
      ? capitalize(category)
      : category.toUpperCase()
    : "";

  const existingAdmissions = patientTimeline.inProgressWork.admissions;

  const existingWorkItems = patientTimeline.inProgressWork.items;

  return (
    <Box
      sx={{
        display: "flex",
      }}
    >
      <NewFormDialog
        formTemplateFilterFn={isNotAdmissionTemplate}
        allowRequestForm={true}
        skipMcaStartPrompts={true}
        patientId={patientTimeline.patient.id}
        title={
          categoryCaps
            ? `Start ${
                categoryCaps === MISC ? acdV1Base.formName : categoryCaps
              } Form`
            : "Start Form"
        }
        closeDialog={() => {
          navigate(routeFns.patientHome(patientTimeline.patient.id));
        }}
        existingAdmissions={existingAdmissions}
        existingWorkItems={existingWorkItems}
        syncExternalPatientLink={syncExternalPatientLink}
        patientTimeline={patientTimeline}
      />
    </Box>
  );
}

const TREATMENT_CATEGORY = "treatment";
const HOSPITAL_CATEGORY = "hospital";
const ADMISSION_CATEGORY = "admission";
const DISCHARGE_CATEGORY = s23V1Base.category;
const MISCELLANEOUS_CATEGORY = m2V1Base.category;
const MISC = acdV1Base.category.toLocaleUpperCase();

export function PatientHomePageInner({
  patientTimeline,
  mode,
  reloadPatientTimeline,
  externalPatientLinks,
  nhsNumber,
}: {
  patientTimeline: PatientTimelineResponse;
  reloadPatientTimeline: () => void;
  mode: "patient" | "form-launch";
  externalPatientLinks?: {
    links: ExternalPatientLinkEnhanced[];
    onSync: (
      externalPatientLink: ExternalPatientLink,
      reason: CreateExternalPatientDemograhicsPullEventRequest["reason"],
    ) => Promise<void>;
  };

  nhsNumber?: string;
}) {
  const userContext = useContext(LoggedInUserContext);
  const { user } = userContext!;
  const navigate = useNavigate();
  const { patientId, category: launchCategory } = useParams();
  const location = useLocation();
  const [showOldForms, setShowOldForms] = useState(true);
  const [expandedForms, setExpandedForms] = useState(true);
  const [showInProgressForms, setShowInProgressForms] = useState(true);

  const isSoad = isSoadUserSession(user);
  const isCqc = isCqcUserSession(user);
  const isSoadOrCqc = isSoad || isCqc;

  // HACK: ACD being enabled (for the KCL pilot) disables all other forms and just shows
  // ACD for now. T
  const isAcdEnabled = user.sessionOrganisationConfiguration?.acdEnabled;
  const isMcaEnabled = user.sessionOrganisationConfiguration?.mcaEnabled;
  const isMcaSession = isMcaUserSession(user);
  const isS62Enabled = user.sessionOrganisationConfiguration?.s62Enabled;
  const areAdmissionsEnabled =
    user.sessionOrganisationConfiguration?.mhaAdmissionsEnabled;
  const canLaunchAdmissions =
    !isGuestUserSession(user) && !isMcaSession && areAdmissionsEnabled;
  const isAdmissionLaunch =
    canLaunchAdmissions && launchCategory === ADMISSION_CATEGORY;
  const enabledForms = user.sessionOrganisationConfiguration?.enabledForms;

  const { t } = useTranslation();

  const formContextId = useMemo(() => v4(), []);

  // Patient-context reconciliation (clickable "unknown" NHS number).
  // Available outside any form context when the Aspire reconciliation flag
  // (+ NHS number / PDS) is enabled and the patient has no NHS number.
  const aspireCanReconcile = canReconcileViaAspireFlag({
    sessionOrganisationConfiguration: user.sessionOrganisationConfiguration,
    patientHasNhsNumber: patientTimeline.patient.nhsNumber != null,
  });

  const isReconciliationRoute =
    !!patientId &&
    location.pathname.endsWith(routeFns.patientHomeReconciliation(patientId));

  const reconcileClickHandler =
    aspireCanReconcile && patientId
      ? () => {
          navigate(routeFns.patientHomeReconciliation(patientId));
        }
      : undefined;

  // Redirect away from the reconciliation route if no longer eligible
  // (e.g. direct URL access, or the patient gained an NHS number).
  useEffect(() => {
    if (!isReconciliationRoute || !patientId) return;
    if (!aspireCanReconcile) {
      navigate(routeFns.patientHome(patientId), { replace: true });
    }
  }, [isReconciliationRoute, aspireCanReconcile, patientId, navigate]);

  const [pdfViewFormId, setPdfViewFormId] = useState<null | string>(null);

  const existingAdmissions = patientTimeline.inProgressWork.admissions;

  const existingAdmissionsLength = !!existingAdmissions?.length;

  const launchAdmission = async () => {
    const result = await api.forms.createAdmissionsContext(
      formContextId,
      patientTimeline.patient.id,
    );

    if (result.status === 204) {
      renderSuccessToast({
        message: `MHA Assessment launched for ${patientTimeline.patient.name.given} ${patientTimeline.patient.name.family}`,
      });
      navigate(
        routeFns.formContextPage(formContextId, patientTimeline.patient.id),
      );
    } else {
      renderErrorToast({
        message: `Failed to launch admission form: ${
          (result.data as any).message
        }`,
      });
    }
  };

  const filteredTemplates = baseTemplates.filter(
    (t) =>
      !admissionTemplateIds.includes(t.id) &&
      enabledForms?.some((f) => f.id === t.id && f.version === t.version),
  );

  const templatesForUser = filteredTemplates.filter((f) => {
    // TODO: sessionContext can actually be an OrganisationMembership or a TeamMembership.
    // Right now it's typed only as ExtendedTeamMembership, so we cast here as a stopgap.
    // Once types are corrected in @thalamos/aspire-common, remove this cast and fix properly.
    if (
      (user.sessionContext as unknown as OrganisationMembership | null)
        ?.type === "organisation"
    ) {
      return false;
    }
    return f.parts.some((part) =>
      doesUserMeetRequirement(
        {
          ...user,
          memberships: user.sessionContext ? [user.sessionContext] : [],
        },
        getViewAndInviteRequirement(part),
      ),
    );
  });

  const templates = (
    isAcdEnabled
      ? templatesForUser
      : templatesForUser.filter((t) => t.id !== "acd")
  )
    .filter((t) => isMcaEnabled || t.category !== "mca")
    .filter((t) => isS62Enabled || t.id !== "mha-s62")
    // An MCA governance team works in a care home: it has no admitting or detaining
    // function, so offering it Section 2/3/4 or Section 20 forms invites a form
    // nobody in that team can lawfully complete.
    .filter((t) => !isMcaSession || t.category === "mca");

  const uniqueTemplateCategories = Object.values(
    templates.reduce<FormAcc>(
      (acc, { category, formName, description, section }) => {
        acc[category] ??= { category, formName, description, section: [] };
        acc[category].section.push(section);
        return acc;
      },
      {},
    ),
  )
    // 136 forms are never directly launchable from patient page
    .filter(({ category }) => category !== "136")
    .map(({ category, ...rest }) => ({
      category: [
        TREATMENT_CATEGORY,
        HOSPITAL_CATEGORY,
        ADMISSION_CATEGORY,
        DISCHARGE_CATEGORY,
        MISCELLANEOUS_CATEGORY,
      ].includes(category)
        ? capitalize(category)
        : category.toUpperCase(),
      ...rest,
    }));

  const inProgressWorkItemsLength = patientTimeline.inProgressWork.items.length;

  const inProgressAdmissions = patientTimeline.inProgressWork?.admissions;

  const inProgressForms =
    !!inProgressWorkItemsLength || !!inProgressAdmissions?.length;
  const completedWorkCount = patientTimeline.completedWork.count;

  (inProgressAdmissions || []).sort(firstBy("updated", -1));

  const closePatientActivityLog = () => {
    navigate(routeFns.patientHome(patientId!));
  };

  // Activity Log modal
  const showActivityLogModal = location.pathname.startsWith(
    routeFns.patientHomeActivityLog(patientId!),
  );

  const syncExternalPatientLink = useCallback(
    async (
      reason: CreateExternalPatientDemograhicsPullEventRequest["reason"],
    ) => {
      if (externalPatientLinks !== undefined) {
        // Prefer Rio link if available as this is the primary demographics source,
        // otherwise use the first link that can pull (probably PDS)
        const linksCanPull = externalPatientLinks.links.filter(
          (l) => l.canPull,
        );

        const externalPatientLink =
          linksCanPull.find((l) => l.externalSystemType === "rio") ??
          linksCanPull[0];

        if (externalPatientLink) {
          await externalPatientLinks.onSync(externalPatientLink, reason);
        }
      }
    },
    [externalPatientLinks],
  );

  const MAX_VISIBLE_FORMS = 10;
  const [showAll, setShowAll] = useState(false);

  const visibleForms = showAll
    ? patientTimeline.completedWork.items
    : patientTimeline.completedWork.items.slice(0, MAX_VISIBLE_FORMS);

  return (
    <>
      <FormTitle
        titleText={t("pages.patientHome.title")}
        useReducedTopPadding={true}
        hasTitleBottomMargin={false}
      />
      <PatientBanner
        patient={patientTimeline.patient}
        patientTimeline={patientTimeline}
        nhsNumber={nhsNumber}
        reloadPatientTimeline={reloadPatientTimeline}
        isHidden={isSoadOrCqc}
        onReconcileClick={reconcileClickHandler}
      />
      {isReconciliationRoute && aspireCanReconcile && patientId && (
        <PatientReconciliationSearchModal
          open={true}
          onClose={() => {
            navigate(routeFns.patientHome(patientId), { replace: true });
          }}
          onComplete={({ patientId: mergedPatientId }) => {
            reloadPatientTimeline();
            navigate(routeFns.patientHome(mergedPatientId), { replace: true });
          }}
          currentPatientId={patientTimeline.patient.id}
          pdsOrg={
            user.sessionOrganisationConfiguration?.pdsEndUserOrganisationODSCode
              ? {
                  odsCode:
                    user.sessionOrganisationConfiguration
                      .pdsEndUserOrganisationODSCode,
                }
              : undefined
          }
          currentPatient={patientTimeline.patient}
          currentPatientNhsNumber={nhsNumber}
        />
      )}
      {uniqueTemplateCategories.length > 0 ? (
        <Container addMarginBottom>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2,
            }}
          >
            <FormTitle
              subtitleText={t("common.newForm")}
              subTitleTextFontSize={2}
              hasContainerMarginBottom={false}
            />
            <ExpandMore
              role="button"
              isExpanded={expandedForms}
              onClick={() => {
                setExpandedForms(!expandedForms);
              }}
              aria-expanded={expandedForms}
              aria-label={t("components.cardExtended.expandMoreLabel")}
            >
              <ExpandMoreIcon sx={{ color: "primary.main" }} />
            </ExpandMore>
          </Box>
          <Collapse in={expandedForms} timeout="auto" unmountOnExit>
            {canLaunchAdmissions && (
              <FormCategoryRow
                title={t("pages.patientHome.admissionFormTitle")}
                subtitle={`${t("pages.patientHome.section")} 2, 3 ${t(
                  "common.and",
                )} 4`}
                launchFormLabel={t("pages.patientHome.launchForm.startForm")}
                buttonTestId="start-form-button--admissions"
                onLaunchForm={async () => {
                  if (!existingAdmissionsLength) {
                    await syncExternalPatientLink("started form");
                    return launchAdmission();
                  }

                  navigate(
                    routeFns.patientFormLaunch(patientId!, ADMISSION_CATEGORY),
                  );
                }}
              />
            )}
            {uniqueTemplateCategories.map((u) => (
              <FormCategoryRow
                key={u.description}
                title={
                  u.category === MISC
                    ? `${u?.description}`
                    : `${u?.category} ${t("common.forms")}`
                }
                subtitle={
                  u.category === "MCA"
                    ? "Mental Capacity Act 2005"
                    : u.category === MISC
                      ? `${t("pages.patientHome.section")} ${u?.section}`
                      : `${t("pages.patientHome.section")} ${u?.section
                          .map((s) => (s === "2, 3 and 4" ? "H3" : s))
                          .filter((s) => s)
                          .join(", ")}`
                }
                launchFormLabel={t("pages.patientHome.launchForm.startForm")}
                buttonTestId={`start-form-button--${u.category.toLowerCase()}`}
                onLaunchForm={() => {
                  const category = u.category.toLowerCase();
                  navigate(routeFns.patientFormLaunch(patientId!, category));
                }}
              />
            ))}
          </Collapse>
        </Container>
      ) : (
        <> </>
      )}
      {mode === "form-launch" && (
        <>
          {isAdmissionLaunch ? (
            <AdmissionWarningDialog
              existingAdmissions={existingAdmissions}
              launchAdmission={launchAdmission}
              patientId={patientId!}
              patientTimeline={patientTimeline}
              syncExternalPatientLink={syncExternalPatientLink}
            />
          ) : (
            <PatientFormLaunch
              patientTimeline={patientTimeline}
              syncExternalPatientLink={syncExternalPatientLink}
            />
          )}
        </>
      )}
      {pdfViewFormId && (
        <FormContextPdfViewer
          inModal={true}
          forms={[
            ...(patientTimeline.inProgressWork.admissions || []),
            ...patientTimeline.inProgressWork.items,
            ...patientTimeline.completedWork.items,
          ]
            .find((i) => i.forms.some((f) => f.id === pdfViewFormId))!
            .forms.filter(
              (f) =>
                f.linkedForms.length > 0 ||
                !admissionTemplateIds.includes(f.formTemplate.id),
            )}
          setFormId={setPdfViewFormId}
          formId={pdfViewFormId}
        />
      )}
      <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
        {inProgressForms && (
          <Container addMarginBottom>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 2,
              }}
            >
              <FormTitle
                subtitleText={`In Progress Forms (${
                  inProgressWorkItemsLength + (existingAdmissions?.length || 0)
                })`}
                subTitleTextFontSize={2}
                hasContainerMarginBottom={false}
              />
              <ExpandMore
                role="button"
                isExpanded={showInProgressForms}
                onClick={() => {
                  setShowInProgressForms(!showInProgressForms);
                }}
                aria-expanded={showInProgressForms}
                aria-label={t("components.cardExtended.expandMoreLabel")}
              >
                <ExpandMoreIcon sx={{ color: "primary.main" }} />
              </ExpandMore>
            </Box>
            {showInProgressForms && (
              <Box sx={{ mt: 2 }}>
                {inProgressAdmissions &&
                  inProgressAdmissions.map((admission, index) => {
                    const merged = findFormsMerged(
                      patientTimeline?.mergedPatientData,
                      admission,
                    );
                    return (
                      <PatientTimelineCard
                        key={index}
                        formContext={admission}
                        setPdfViewFormId={setPdfViewFormId}
                        lastMerged={merged?.lastMerged}
                      />
                    );
                  })}
                {patientTimeline.inProgressWork.items.map((formContext) => {
                  const merged = findFormsMerged(
                    patientTimeline?.mergedPatientData,
                    formContext,
                  );

                  return (
                    <PatientTimelineCard
                      key={formContext.id}
                      formContext={formContext}
                      setPdfViewFormId={setPdfViewFormId}
                      lastMerged={merged?.lastMerged}
                    />
                  );
                })}
              </Box>
            )}
          </Container>
        )}
      </Box>
      {completedWorkCount > 0 && (
        <Container>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2,
            }}
          >
            <FormTitle
              hasContainerMarginBottom={false}
              subtitleText={`Old forms (${completedWorkCount})`}
              subTitleTextFontSize={2}
            />
            <ExpandMore
              role="button"
              isExpanded={showOldForms}
              onClick={() => {
                setShowOldForms(!showOldForms);
              }}
              aria-expanded={showOldForms}
              aria-label={t("components.cardExtended.expandMoreLabel")}
            >
              <ExpandMoreIcon sx={{ color: "primary.main" }} />
            </ExpandMore>
          </Box>
          {showOldForms &&
            visibleForms.map((formContext) => {
              const merged = findFormsMerged(
                patientTimeline?.mergedPatientData,
                formContext,
              );

              return (
                <Box key={formContext.id}>
                  <PatientTimelineCard
                    formContext={formContext}
                    setPdfViewFormId={setPdfViewFormId}
                    lastMerged={merged?.lastMerged}
                  />
                </Box>
              );
            })}

          {showOldForms &&
            patientTimeline.completedWork.items.length > MAX_VISIBLE_FORMS && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Button
                  label={
                    showAll
                      ? t("pages.patientHome.viewLess")
                      : t("pages.patientHome.viewMore")
                  }
                  onClick={() => {
                    setShowAll(!showAll);
                  }}
                  variant="contained"
                  sx={{ mt: 2 }}
                />
              </Box>
            )}
        </Container>
      )}
      {showActivityLogModal && (
        <PatientActivityLog
          patient={patientTimeline.patient}
          patientId={patientId!}
          user={user}
          onClose={closePatientActivityLog}
        />
      )}
      <FormFooterSection
        hideSubmit={true}
        isSticky={true}
        onSave={() => {}}
        disableSubmit={true}
        saveLabel={t("buttonLabels.send")}
        discardLabel={t("buttonLabels.patientLog")}
        onCancel={() => {
          navigate(routeFns.patientHomeActivityLog(patientId!));
        }}
      />
    </>
  );
}

export function PatientHomePage({}: DefaultPageProps) {
  const { patientId } = useParams();

  if (!isRecordId(patientId)) {
    return <Navigate to={routeFns.notFound()} replace />;
  }

  return <PatientHomePageForPatientId />;
}

function PatientHomePageForPatientId() {
  const { patientId, externalLinkId: externalLinkIdFromParams } = useParams();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const page = 0;

  const { patientTimeline, response, error, reloadPatientTimeline } =
    usePatientTimeline({
      patientId: patientId,
      page: page,
      limit: 1000,
    });

  const {
    externalPatientLinks,
    externalPatientLinksLoading,
    syncExternalPatientLink,
    reloadExternalPatientLinks,
    Component: ExternalPatientLinkComponents,
    patientForReview: hasShownPatientForReview,
  } = useExternalPatientLinks({
    patientId: patientId!,
  });

  const nhsNumber = patientTimeline?.patient?.nhsNumber;

  const [mode, setMode] = useState<"patient" | "form-launch">("patient");

  const { pathname } = useLocation();

  useEffect(() => {
    if (pathname.includes("form-launch")) {
      setMode("form-launch");
    } else {
      setMode("patient");
    }
  }, [pathname]);

  useEffect(() => {
    if (isNotFoundStatus(response?.status)) {
      navigate(routeFns.notFound());
    }
  }, [response, patientId, navigate]);

  const onSync = useCallback(
    async (
      externalPatientLink: ExternalPatientLink,
      reason: CreateExternalPatientDemograhicsPullEventRequest["reason"],
    ) => {
      await syncExternalPatientLink(externalPatientLink, reason);
      reloadPatientTimeline();
      reloadExternalPatientLinks();
    },
    [syncExternalPatientLink],
  );

  useEffect(() => {
    if (
      response?.status === 200 &&
      externalPatientLinksLoading === false &&
      externalPatientLinks &&
      externalLinkIdFromParams &&
      patientId
    ) {
      const link = externalPatientLinks.externalLinks.find(
        (l) => l.id === externalLinkIdFromParams,
      );

      if (!link) {
        navigate(routeFns.patientHome(patientId), { replace: true });
      } else {
        onSync(link, "click through")
          .then(() => {
            navigate(routeFns.patientHome(patientId), { replace: true });
          })
          .catch(() => {
            navigate(routeFns.patientHome(patientId), { replace: true });
          });
      }
    }
  }, [
    onSync,
    externalPatientLinksLoading,
    externalPatientLinks,
    externalLinkIdFromParams,
    response?.status,
    patientId,
    navigate,
  ]);

  // Kept apart from the 200 check below: a failed read must throw before the
  // external-link branch, but a read in flight must still reach its spinner.
  throwUnansweredReadFailure({ error, response }, "patient");

  // Show a loading spinner while loading from the external system
  if (externalLinkIdFromParams) {
    return hasShownPatientForReview ? (
      <ExternalPatientLinkComponents />
    ) : (
      <Stack
        sx={{
          gap: "2rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="h5">
          {t("pages.patientHome.connectingToExternalSystem")}
        </Typography>
        <CircularProgress />
        <Link to={routeFns.patientHome(patientId!)}>
          {t("pages.patientHome.returnToPatientHome")}
        </Link>
      </Stack>
    );
  }

  if (response?.status !== 200) {
    return <></>;
  }

  return (
    <>
      <ExternalPatientLinkComponents />
      <PatientHomePageInner
        patientTimeline={patientTimeline!}
        mode={mode}
        reloadPatientTimeline={reloadPatientTimeline}
        nhsNumber={nhsNumber ?? undefined}
        externalPatientLinks={
          externalPatientLinksLoading === false &&
          externalPatientLinks?.externalLinks !== undefined
            ? {
                links: externalPatientLinks?.externalLinks,
                onSync,
              }
            : undefined
        }
      />
    </>
  );
}
